import { createContext, use, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { useSiteName } from './site-name'

/** A record page's header: its heading and the line above it, one of which names the record. */
export interface RecordHeading {
  readonly heading: string
  readonly eyebrow: string
}

interface PageTitleSetters {
  readonly setOverride: (title: string | null) => void
  readonly setRecord: (record: RecordHeading | null) => void
}

const PageTitleContext = createContext<PageTitleSetters>({ setOverride: () => undefined, setRecord: () => undefined })

/**
 * The record's name in a record page's header: the heading, unless the heading only repeats the
 * route's title, as "Manage Files" under a class's name, when the eyebrow above it is the name.
 */
function recordName(record: RecordHeading | null, route: string): string | null {
  const name = record === null ? null : record.heading !== route ? record.heading : record.eyebrow
  return name === null || name.trim() === '' || name === route ? null : name
}

/**
 * Sets the browser tab's title to the route's own, or to what the page gives
 * {@link usePageTitle}, followed by the site's name. A page about one record, such as a class's
 * roster, adds the record's name through {@link useRecordTitle} ("Roster: QA Class"), so each
 * record's pages and tabs can be told apart (WCAG 2.4.2).
 */
export function RouteTitle(props: { readonly titleId: string; readonly children: ReactNode }) {
  const intl = useIntl()
  const [override, setOverride] = useState<string | null>(null)
  const [record, setRecord] = useState<RecordHeading | null>(null)
  const route = intl.formatMessage({ id: props.titleId })
  const name = recordName(record, route)
  const page = override ?? (name === null ? route : intl.formatMessage({ id: 'shell.recordTitle' }, { page: route, record: name }))
  const site = useSiteName() ?? intl.formatMessage({ id: 'shell.defaultSiteName' })
  const setters = useMemo(() => ({ setOverride, setRecord }), [])

  useEffect(() => {
    document.title = intl.formatMessage({ id: 'shell.documentTitle' }, { page, site })
  }, [intl, page, site])

  return <PageTitleContext value={setters}>{props.children}</PageTitleContext>
}

/** Replaces the route's title while the calling page is shown; `undefined` keeps the route's. */
export function usePageTitle(title: string | undefined): void {
  const { setOverride } = use(PageTitleContext)

  useEffect(() => {
    if (title === undefined) {
      return undefined
    }
    setOverride(title)
    return () => {
      setOverride(null)
    }
  }, [setOverride, title])
}

/**
 * Adds the name of the record the calling page is about, from its header, to the route's title
 * while it is shown; `undefined` for a header that names no record.
 */
export function useRecordTitle(heading: string | undefined, eyebrow: string | undefined): void {
  const { setRecord } = use(PageTitleContext)

  useEffect(() => {
    if (heading === undefined || eyebrow === undefined) {
      return undefined
    }
    setRecord({ heading, eyebrow })
    return () => {
      setRecord(null)
    }
  }, [setRecord, heading, eyebrow])
}
